import { useState } from "react";
import { ArrowRight, FolderOpen, ImageSquare, Plus, UploadSimple, X } from "@phosphor-icons/react";
import type { PhotoFolioProject } from "../domain/project";

interface HomeProps {
  projects: PhotoFolioProject[];
  onCreate: (details: { title: string; subtitle: string; date: string; location: string; people: string[] }) => void;
  onOpen: (project: PhotoFolioProject) => void;
  onImport: (file: File) => void;
  onDemo: () => void;
}

export function Home({ projects, onCreate, onOpen, onImport, onDemo }: HomeProps) {
  const [creating, setCreating] = useState(false);
  const [details, setDetails] = useState({ title: "", subtitle: "", date: "", location: "", people: "" });
  const confirmCreate = () => {
    onCreate({
      title: details.title.trim() || "未命名影集",
      subtitle: details.subtitle.trim() || "把经过的光，留在这里。",
      date: details.date,
      location: details.location.trim(),
      people: details.people.split(/[，,]/).map((item) => item.trim()).filter(Boolean),
    });
    setCreating(false);
  };
  return (
    <main className="home-shell">
      <nav className="home-nav">
        <a className="brand" href="#top" aria-label="PhotoFolio 首页"><span className="brand-mark">PF</span><span>PhotoFolio</span></a>
        <label className="text-button file-button">
          <UploadSimple size={18} />导入草稿
          <input type="file" accept=".photofolio,.zip" onChange={(event) => event.target.files?.[0] && onImport(event.target.files[0])} />
        </label>
      </nav>
      <section className="home-hero" id="top">
        <div className="hero-copy">
          <span className="eyebrow">本地优先的影集工作台</span>
          <h1><span>照片离开云端，</span><span>故事留在手边。</span></h1>
          <p>整理照片，写下记忆，导出一个断网也能阅读的完整网页。</p>
          <div className="hero-actions">
            <button className="primary-button" type="button" onClick={() => setCreating(true)}><Plus size={19} weight="bold" />开始整理照片</button>
            <button className="secondary-button" type="button" onClick={onDemo}><ImageSquare size={19} />打开示例</button>
          </div>
        </div>
        <div className="hero-visual" aria-label="PhotoFolio 影集样式预览">
          <div className="folio-sheet sheet-back" />
          <div className="folio-sheet sheet-front">
            <div className="folio-photo"><span>LIGHT<br />STAYS</span></div>
            <div className="folio-caption">保存照片，也保存它们之间的顺序。</div>
          </div>
        </div>
      </section>
      <section className="project-section">
        <div className="section-title"><h2>最近的影集</h2><span>{projects.length} 个项目保存在此浏览器</span></div>
        {projects.length ? (
          <div className="project-grid">
            {projects.map((project) => (
              <button className="project-tile" type="button" key={project.id} onClick={() => onOpen(project)}>
                <span className="project-index">{String(project.items.length).padStart(2, "0")} 张照片</span>
                <span className="project-name">{project.title}</span>
                <span className="project-subtitle">{project.subtitle}</span>
                <span className="project-open">继续编辑 <ArrowRight size={18} /></span>
              </button>
            ))}
          </div>
        ) : (
          <button className="empty-projects" type="button" onClick={() => setCreating(true)}>
            <FolderOpen size={32} />
            <strong>还没有影集</strong>
            <span>创建第一个项目，照片只会保存在你的浏览器中。</span>
          </button>
        )}
      </section>
      <footer className="home-footer"><span>PhotoFolio</span><span>不上传照片，不依赖账号。</span></footer>
      {creating ? <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setCreating(false)}>
        <section className="creation-dialog" role="dialog" aria-modal="true" aria-labelledby="creation-title">
          <div className="dialog-head"><div><span>创建一本新的影集</span><h2 id="creation-title">先给这段记忆一个名字</h2></div><button className="icon-button" type="button" aria-label="关闭" onClick={() => setCreating(false)}><X size={20} /></button></div>
          <div className="creation-fields">
            <label><span>相册名称</span><input autoFocus value={details.title} placeholder="例如：北海道的七个清晨" onChange={(event) => setDetails({ ...details, title: event.target.value })} /></label>
            <label><span>副标题，可稍后填写</span><input value={details.subtitle} placeholder="一句想写在封面上的话" onChange={(event) => setDetails({ ...details, subtitle: event.target.value })} /></label>
            <div className="field-pair">
              <label><span>日期</span><input type="date" value={details.date} onChange={(event) => setDetails({ ...details, date: event.target.value })} /></label>
              <label><span>地点</span><input value={details.location} placeholder="例如：北海道" onChange={(event) => setDetails({ ...details, location: event.target.value })} /></label>
            </div>
            <label><span>同行人物，可选</span><input value={details.people} placeholder="用逗号分隔姓名" onChange={(event) => setDetails({ ...details, people: event.target.value })} /></label>
          </div>
          <div className="dialog-note">照片只会在你的浏览器中处理。所有信息都可以稍后修改。</div>
          <div className="dialog-actions"><button className="secondary-button" type="button" onClick={() => setCreating(false)}>稍后再说</button><button className="primary-button" type="button" onClick={confirmCreate}>进入工作台 <ArrowRight size={18} /></button></div>
        </section>
      </div> : null}
    </main>
  );
}
